import { useState, useCallback } from 'react';
import api from '../lib/api';
import { User } from '../types';

// Safely parse the persisted user object — handles the "undefined" string
// bug (when JSON.stringify(undefined) was stored previously).
function readStoredUser(): User | null {
  try {
    const stored = localStorage.getItem('user');
    if (!stored || stored === 'undefined' || stored === 'null') return null;
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    // Bad value in storage — clear it so future reads work
    try { localStorage.removeItem('user'); } catch { /* noop */ }
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(readStoredUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/auth/login', { email, password });
      // מנהלים (org_admin / super_admin) מקבלים שלב שני: הסיסמה אומתה,
      // קוד נשלח למייל, והשרת החזיר preAuthToken במקום token. הדף מציג
      // שדה קוד וקורא ל-completeTwoFa להשלמת הכניסה.
      if (response.data?.otpRequired) {
        return { otpRequired: true, preAuthToken: response.data.preAuthToken } as any;
      }
      const { token, user: userData } = response.data || {};
      if (!token || !userData) {
        throw new Error('תשובת שרת לא תקינה — חסרים token או user');
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // שלב שני של כניסת מנהלים — preAuthToken מ-login + הקוד מהמייל.
  const completeTwoFa = useCallback(async (preAuthToken: string, code: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/auth/login-2fa', { preAuthToken, code });
      const { token, user: userData } = response.data || {};
      if (!token || !userData) {
        throw new Error('תשובת שרת לא תקינה — חסרים token או user');
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    name: string,
    orgData?: {
      orgName: string;
      orgRegistrationNumber: string;
      orgEmail: string;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        name,
        ...orgData,
      });
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err: any) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';
  const isOrgAdmin = user?.role === 'org_admin';

  return { user, loading, error, login, completeTwoFa, register, logout, isSuperAdmin, isOrgAdmin };
}
